import React, { useEffect, useState } from "react";

import "./styles.css";
import { getRole } from "../../Core/utils/auth";

import { useGetWorksheetFunctions } from "./hooks/useGetWorksheetFuntions";
import { InputBox } from "./components/WorkSheetInputComponents";
import { PreviewBox } from "./components/AdminControls";
import { getHtmlBoxesData } from "./utils";

const InteractiveWorksheet = ({
  imageUrls,
  boxes = [],
  isAdmin = false,
  onBoxesChange,
  isExerciseAttempted = false,
  showAnswers = false,
  exersiceDetails,
  isImported,
  styles = {
    size: "16px",
    color: "black",
  },
}) => {
  const {
    methods,
    states: { selectedBoxId, isCreating, previewBox },
    refs: { containerRef },
  } = useGetWorksheetFunctions(
    boxes,
    onBoxesChange,
    isAdmin,
    exersiceDetails,
    isExerciseAttempted
  );

  const handleBlur = () => {
    if (!showAnswers) return;
    setTimeout(() => {
      if (document.activeElement !== containerRef.current) {
        containerRef.current?.focus();
      }
    }, 0);
  };

  useEffect(() => {
    containerRef.current?.focus();
  }, [containerRef]);

  const isPx = isImported;

  return (
    <div className="worksheet-wrapper" id="worksheet-wrapper" tabIndex={0}>
      <div
        ref={containerRef}
        className={`worksheet-container ${isCreating ? "creating" : ""}`}
        style={{
          margin: "auto",
          width: isPx ? "fit-content" : "100%",
        }}
        tabIndex={0}
        onBlur={handleBlur}
      >
        <div
          className="multi-page-worksheet"
          style={{
            width: isPx ? "1000px" : "100%",
          }}
        >
          {imageUrls?.map((url, index) => (
            <img
              key={`page-${index}`}
              src={url}
              alt={`Worksheet Page ${index + 1}`}
              className="worksheet-image"
              draggable={false}
              onClick={methods.handleContainerClick}
              style={{
                width: isPx ? "1000px" : "100%",
              }}
            />
          ))}
        </div>

        {boxes.map((box) => (
          <InputBox
            key={box.id}
            box={box}
            isAdmin={isAdmin}
            isSelected={selectedBoxId === box.id}
            onSelect={methods.handleBoxSelect}
            onUpdate={methods.handleBoxUpdate}
            onDelete={methods.handleBoxDelete}
            onCopy={methods.handleBoxCopy}
            onBlur={methods.handleSaveDraft}
            containerRef={containerRef}
            styles={styles}
            showAnswers={showAnswers}
            isExerciseAttempted={isExerciseAttempted}
            isPx={isPx}
          />
        ))}

        {isCreating && <PreviewBox previewBox={previewBox} />}
      </div>
    </div>
  );
};

// Main export component
export const WorksheetCanvas = ({
  exercise,
  showAnswers = false,
  boxes,
  setBoxes,
  preview = false,
  exerciseId = "",
}) => {
  const [htmlBoxData, setHtmlBoxData] = useState([]);
  const role = getRole();
  const isAdmin = preview ? false : role === "admin";
  const isExerciseAttempted =
    exercise?.answers !== null &&
    exercise?.submission?.attempted > 0 &&
    Object.keys(exercise?.answers || {}).length > 0;

  const backgroundUrls = exercise?.properties?.backgroundUrl;

  let exersiceDetails = null;

  if (exerciseId !== "" && exercise) {
    exersiceDetails = {
      exerciseId: exerciseId || "",
      pageId: exercise?.pageId || "",
      hasDraft: exercise?.hasDraft || false,
      answers: exercise?.answers,
      isExerciseAttempted,
    };
  }

  useEffect(() => {
    if (
      exercise?.originalState === "" ||
      exercise?.originalState === undefined ||
      exercise?.originalState === null
    )
      return;
    getHtmlBoxesData(exercise?.originalState, setHtmlBoxData);
  }, [boxes.length, exercise?.originalState]);

  useEffect(() => {
    if (boxes.length !== 0 && htmlBoxData.length !== 0) {
      const updateBoxes = boxes.map((box) => {
        const { style, isCorrupted } = htmlBoxData[box.key] || {};
        if (isCorrupted) {
          box["boxCoords"] = style;
        }
        return box;
      });
      setBoxes(updateBoxes);
    }
  }, [exercise?.originalState, htmlBoxData.length, JSON.stringify(boxes)]);

  const isImported = exercise?.properties?.imported === 1 ? true : false;

  return (
    <InteractiveWorksheet
      imageUrls={backgroundUrls}
      boxes={boxes}
      isAdmin={isAdmin}
      showAnswers={showAnswers}
      isExerciseAttempted={isExerciseAttempted}
      onBoxesChange={setBoxes}
      styles={exercise?.properties?.style}
      exersiceDetails={exersiceDetails}
      isImported={isImported}
    />
  );
};
